import {
    ActivityCustomFields,
    FerryCustomFields,
    FlightCustomFields,
    GolfCustomFields,
    HotelCustomFields,
} from '@/tracking/analytics/Checkout/types';
import {
    MyBookingHotelCustomFields,
    MyBookingFlightCustomFields,
    MyBookingFerryCustomFields,
    MyBookingActivityCustomFields,
    MyBookingGolfCustomFields,
    MyBookingTransferCustomFields,
} from '@/tracking/analytics/MyBooking/types';

interface Ecommerce {
    currency: string;
    value: number;
    margin?: number;
    coupon?: string;
    payment_type?: string;
    transaction_id?: string;
    booking_type?: string;
    published_trip_id?: string;
    related_hotel_ids?: string[];
    items: Item[];
}

export type Event =
    | MyBookingPageInitiatedEvent
    | MyBookingPagePaymentEvent
    | MyBookingsPageInitiatedEvent
    | MetaLandingPageInitiatedEvent
    | MetaLandingAddProductEvent
    | MetaLandingPageCompletedEvent
    | PreCheckoutPageInitiatedEvent
    | PreCheckoutAddProductEvent
    | PreCheckoutRemoveProductEvent
    | PreCheckoutPageCompletedEvent
    | CheckoutPageInitiatedEvent
    | CheckoutCustomerDetailsInitiatedEvent
    | CheckoutCustomerDetailsCompletedEvent
    | CheckoutCustomerEmailCompletedEvent
    | CheckoutAddProductEvent
    | CheckoutRemoveProductEvent
    | CheckoutCartExpired
    | SkipPostCheckoutPageEvent
    | PostCheckoutPageInitiatedEvent
    | PostCheckoutPageCompletedEvent
    | PaymentPageInitiatedEvent
    | PaymentAddProductEvent
    | PaymentRemoveProductEvent
    | PaymentMethodCompletedEvent
    | PaymentMethodInitiatedEvent
    | AmeliaInteractionEvent
    | AmeliaSendFeedbackEvent;

export type DimensionEvent =
    | MyBookingUpsellShownEvent
    | MyBookingUpsellOpenEvent
    | MyBookingUpsellClosedEvent
    | MyBookingUpsellCompletedEvent;

export enum EventName {
    MY_BOOKING_PAGE_INITIATED = 'my_booking_page_initiated',
    MY_BOOKING_PAGE_PAYMENT = 'my_booking_page_payment',
    MY_BOOKING_UPSELL_SHOWN = 'my_booking_upsell_shown',
    MY_BOOKING_UPSELL_OPEN = 'my_booking_upsell_open',
    MY_BOOKING_UPSELL_CLOSED = 'my_booking_upsell_closed',
    MY_BOOKING_UPSELL_COMPLETED = 'my_booking_upsell_completed',
    MY_BOOKINGS_PAGE_INITIATED = 'my_bookings_page_initiated',
    META_LANDING_PAGE_INITIATED = 'meta_landing_page_initiated',
    META_LANDING_ADD_PRODUCT = 'meta_landing_add_product',
    META_LANDING_PAGE_COMPLETED = 'meta_landing_page_completed',
    PRE_CHECKOUT_PAGE_INITIATED = 'pre_checkout_page_initiated',
    PRE_CHECKOUT_ADD_PRODUCT = 'pre_checkout_add_product',
    PRE_CHECKOUT_REMOVE_PRODUCT = 'pre_checkout_remove_product',
    PRE_CHECKOUT_PAGE_COMPLETED = 'pre_checkout_page_completed',
    CHECKOUT_PAGE_INITIATED = 'checkout_page_initiated',
    CHECKOUT_CUSTOMER_DETAILS_INITIATED = 'checkout_customer_details_initiated',
    CHECKOUT_CUSTOMER_DETAILS_COMPLETED = 'checkout_customer_details_completed',
    CHECKOUT_CUSTOMER_EMAIL_COMPLETED = 'checkout_customer_email_completed',
    CHECKOUT_ADD_PRODUCT = 'checkout_add_product',
    CHECKOUT_REMOVE_PRODUCT = 'checkout_remove_product',
    CHECKOUT_CART_EXPIRED = 'checkout_cart_expired',
    SKIP_POST_CHECKOUT_PAGE = 'skip_post_checkout_page',
    POST_CHECKOUT_PAGE_INITIATED = 'post_checkout_page_initiated',
    POST_CHECKOUT_PAGE_COMPLETED = 'post_checkout_page_completed',
    PAYMENT_PAGE_INITIATED = 'payment_page_initiated',
    PAYMENT_ADD_PRODUCT = 'payment_add_product',
    PAYMENT_REMOVE_PRODUCT = 'payment_remove_product',
    PAYMENT_METHOD_INITIATED = 'payment_method_initiated',
    PAYMENT_METHOD_COMPLETED = 'payment_method_completed',
    AMELIA_INTERACTION = 'amelia_interaction',
    AMELIA_SEND_FEEDBACK = 'amelia_send_feedback',
}

export enum BookingType {
    Accommodation = 'ACCOMMODATION',
    AccommodationActivity = 'ACCOMMODATION+ACTIVITY',
    AccommodationActivityFerry = 'ACCOMMODATION+ACTIVITY+FERRY',
    AccommodationActivityFerryFlight = 'ACCOMMODATION+ACTIVITY+FERRY+FLIGHT',
    AccommodationActivityFlight = 'ACCOMMODATION+ACTIVITY+FLIGHT',
    AccommodationFerry = 'ACCOMMODATION+FERRY',
    AccommodationFerryFlight = 'ACCOMMODATION+FERRY+FLIGHT',
    AccommodationFlight = 'ACCOMMODATION+FLIGHT',
    Activity = 'ACTIVITY',
    ActivityFerry = 'ACTIVITY+FERRY',
    ActivityFlight = 'ACTIVITY+FLIGHT',
    Ferry = 'FERRY',
    Flight = 'FLIGHT',
    Other = 'OTHER',
}

interface MyBookingPageInitiatedEvent {
    event: EventName.MY_BOOKING_PAGE_INITIATED;
    ecommerce: Ecommerce;
    isLoggedIn: boolean;
}

interface MyBookingUpsellShownEvent {
    event: EventName.MY_BOOKING_UPSELL_SHOWN;
    type: string;
}

interface MyBookingUpsellOpenEvent {
    event: EventName.MY_BOOKING_UPSELL_OPEN;
    type: string;
}

interface MyBookingUpsellClosedEvent {
    event: EventName.MY_BOOKING_UPSELL_CLOSED;
    type: string;
}

interface MyBookingUpsellCompletedEvent {
    event: EventName.MY_BOOKING_UPSELL_COMPLETED;
    type: string;
}

interface MyBookingPagePaymentEvent {
    event: EventName.MY_BOOKING_PAGE_PAYMENT;
    ecommerce: Ecommerce;
}

interface MyBookingsPageInitiatedEvent {
    event: EventName.MY_BOOKINGS_PAGE_INITIATED;
}

interface CheckoutCustomerDetailsCompletedEvent {
    event: EventName.CHECKOUT_CUSTOMER_DETAILS_COMPLETED;
    ecommerce: Ecommerce;
}

interface CheckoutCustomerEmailCompletedEvent {
    event: EventName.CHECKOUT_CUSTOMER_EMAIL_COMPLETED;
    ecommerce: Ecommerce;
}

interface CheckoutCustomerDetailsInitiatedEvent {
    event: EventName.CHECKOUT_CUSTOMER_DETAILS_INITIATED;
    ecommerce: Ecommerce;
}

interface PreCheckoutPageInitiatedEvent {
    event: EventName.PRE_CHECKOUT_PAGE_INITIATED;
    ecommerce: Ecommerce;
}

interface MetaLandingPageInitiatedEvent {
    event: EventName.META_LANDING_PAGE_INITIATED;
    related_hotel_ids: string[];
    ecommerce: Ecommerce;
}

interface PostCheckoutPageInitiatedEvent {
    event: EventName.POST_CHECKOUT_PAGE_INITIATED;
    ecommerce: Ecommerce;
}

interface PostCheckoutPageCompletedEvent {
    event: EventName.POST_CHECKOUT_PAGE_COMPLETED;
    ecommerce: Ecommerce;
}

interface PreCheckoutPageCompletedEvent {
    event: EventName.PRE_CHECKOUT_PAGE_COMPLETED;
    ecommerce: Ecommerce;
}

interface MetaLandingPageCompletedEvent {
    event: EventName.META_LANDING_PAGE_COMPLETED;
    ecommerce: Ecommerce;
}

interface CheckoutPageInitiatedEvent {
    event: EventName.CHECKOUT_PAGE_INITIATED;
    ecommerce: Ecommerce;
    isLoggedIn: boolean;
}

interface SkipPostCheckoutPageEvent {
    event: EventName.SKIP_POST_CHECKOUT_PAGE;
    ecommerce: Ecommerce;
}

interface CheckoutCartExpired {
    event: EventName.CHECKOUT_CART_EXPIRED;
    ecommerce: Ecommerce;
}

interface PaymentMethodCompletedEvent {
    event: EventName.PAYMENT_METHOD_COMPLETED;
    ecommerce: Ecommerce;
}

interface PaymentPageInitiatedEvent {
    event: EventName.PAYMENT_PAGE_INITIATED;
    ecommerce: Ecommerce;
}

interface PaymentMethodInitiatedEvent {
    event: EventName.PAYMENT_METHOD_INITIATED;
    ecommerce: Ecommerce;
}

interface CheckoutAddProductEvent {
    event: EventName.CHECKOUT_ADD_PRODUCT | EventName.PAYMENT_ADD_PRODUCT;
    ecommerce: Ecommerce;
}

interface CheckoutRemoveProductEvent {
    event: EventName.CHECKOUT_REMOVE_PRODUCT | EventName.PAYMENT_REMOVE_PRODUCT;
    ecommerce: Ecommerce;
}

interface PaymentAddProductEvent {
    event: EventName.PAYMENT_ADD_PRODUCT;
    ecommerce: Ecommerce;
}

interface PaymentRemoveProductEvent {
    event: EventName.PAYMENT_REMOVE_PRODUCT;
    ecommerce: Ecommerce;
}

interface PreCheckoutAddProductEvent {
    event: EventName.PRE_CHECKOUT_ADD_PRODUCT;
    ecommerce: Ecommerce;
}

interface MetaLandingAddProductEvent {
    event: EventName.META_LANDING_ADD_PRODUCT;
    ecommerce: Ecommerce;
}

interface PreCheckoutRemoveProductEvent {
    event: EventName.PRE_CHECKOUT_REMOVE_PRODUCT;
    ecommerce: Ecommerce;
}

interface AmeliaInteractionEvent extends RecommendedData {
    event: EventName.AMELIA_INTERACTION;
    amelia_interaction_type: string;
    amelia_category: string | null;
    amelia_article: string | null;
}

interface AmeliaSendFeedbackEvent extends RecommendedData {
    event: EventName.AMELIA_SEND_FEEDBACK;
    amelia_feedback_message: string;
    amelia_category: string | null;
    amelia_article: string | null;
}

export interface RecommendedData {
    hostname: string;
    href: string;
    booking_id: string | null;
    booking_number: string | null;
}

export interface Item extends CategoriesItem, CustomFields {
    item_id: string;
    item_name: string;
    affiliation?: string;
    coupon?: string;
    discount?: number;
    index?: number;
    item_brand?: string;
    item_list_id?: string;
    item_list_name?: string;
    item_variant?: string;
    location_id?: string;
    price?: number;
    quantity?: number;
}

interface CategoriesItem {
    item_category?: string;
    item_category2?: string;
    item_category3?: string;
    item_category4?: string;
    item_category5?: string;
}

interface CustomFields {
    item_parent_id?: string;
    hotel?: HotelCustomFields;
    flight?: FlightCustomFields;
    activity?: ActivityCustomFields;
    ferry?: FerryCustomFields;
    golf?: GolfCustomFields;
    my_booking_hotel?: MyBookingHotelCustomFields;
    my_booking_flight?: MyBookingFlightCustomFields;
    my_booking_ferry?: MyBookingFerryCustomFields;
    my_booking_activity?: MyBookingActivityCustomFields;
    my_booking_golf?: MyBookingGolfCustomFields; //to remove?
    my_booking_transfer?: MyBookingTransferCustomFields;
}
